import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { storyCanonicalAndAlternates } from "@/lib/hreflang";
import { EsNav } from "@/components/site/EsNav";
import { ShareButtons } from "@/components/site/ShareButtons";
import {
  findStoryBySlug,
  largestUrl,
  resolveLegacyStorySlug,
  storyCreditSchema,
  storyImageObject,
} from "@/lib/results-stories";
import { esStoryCopy, esStoryDescription, esStoryTitle } from "@/lib/es-stories";
import { PHYSICIAN_REF, ORG_REF } from "@/lib/medical-schema";
import { SITE } from "@/lib/es-pages";
import { lastmodFor } from "@/lib/route-lastmod";

function abs(url: string) {
  return url.startsWith("http") ? url : `${SITE}${url}`;
}

export const Route = createFileRoute("/es/antes-y-despues_/historia/$slug")({
  loader: ({ params }) => {
    const found = findStoryBySlug(params.slug);
    if (!found) {
      // Renamed story: 301 to the current Spanish URL so shared links keep
      // their value. Unknown slugs stay a real 404.
      const current = resolveLegacyStorySlug(params.slug);
      if (current) {
        throw redirect({
          to: "/es/antes-y-despues/historia/$slug",
          params: { slug: current },
          statusCode: 301,
        });
      }
      throw notFound();
    }
    return found;
  },
  head: ({ params, loaderData }) => {
    const story = loaderData?.story;
    const index = loaderData?.index ?? 0;
    if (!story) return {};

    const copy = esStoryCopy(story, index);
    const url = `${SITE}/es/antes-y-despues/historia/${params.slug}`;
    const enUrl = `${SITE}/results/story/${params.slug}`;
    const title = esStoryTitle(copy);
    const description = esStoryDescription(copy);
    const image = `${SITE}/og/stories/es/${params.slug}.jpg`;
    const alt = `${copy.label} ${
      story.single ? "antes y después" : "después"
    } de su cirugía de pérdida de peso con el Dr. Matthew Fitzer en Virginia Bariatric Surgery`;

    return {
      meta: [
      { title },
      { name: "description", content: description },
      // Individual story pages are short, near-identical photo captions. They
      // stay live and shareable, but are kept out of the search index so the
      // /es/antes-y-despues gallery carries the ranking signal instead.
      { name: "robots", content: "noindex, follow" },
      ...(import.meta.env.SSR ? [
        { property: "og:site_name", content: "Virginia Bariatric Surgery" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "es_LA" },
        { property: "og:locale:alternate", content: "en_US" },
        { property: "og:image", content: image },
        { property: "og:image:alt", content: alt },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "2400" },
        { property: "og:image:height", content: "1260" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
        { name: "twitter:image:alt", content: alt },
      ] : []),
    ],
      links: [
        ...storyCanonicalAndAlternates(params.slug, "es"),
      ],
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id": `${url}#webpage`,
                translationOfWork: { "@id": `${enUrl}#webpage` },
                name: title,
                description,
                url,
                inLanguage: "es-US",
                specialty: "https://schema.org/Surgical",
                lastReviewed: lastmodFor(`/es/antes-y-despues/historia/${params.slug}`) ?? "2026-08-09",
                reviewedBy: PHYSICIAN_REF,
                publisher: ORG_REF,
                mainEntity: { "@id": `${url}#article` },
                primaryImageOfPage: { "@id": `${url}#primaryimage` },
                isPartOf: { "@id": `${SITE}/es/antes-y-despues#webpage` },
              },
              storyImageObject({
                id: `${url}#primaryimage`,
                img: story.single ? story.before : story.after,
                story,
                name: story.single
                  ? `${copy.label} — antes y después de la cirugía de pérdida de peso`
                  : `${copy.label} — después de la cirugía de pérdida de peso`,
                caption: copy.caption,
                representativeOfPage: true,
                isPartOf: `${url}#gallery`,
              }),
              ...(story.single
                ? []
                : [
                    storyImageObject({
                      id: `${url}#beforeimage`,
                      img: story.before,
                      story,
                      name: `${copy.label} — antes de la cirugía de pérdida de peso`,
                      caption: `${copy.label} antes de su cirugía de pérdida de peso con el Dr. Matthew Fitzer.`,
                      isPartOf: `${url}#gallery`,
                    }),
                  ]),
              {
                "@type": "ImageObject",
                "@id": `${url}#socialimage`,
                contentUrl: image,
                url: image,
                caption: alt,
                encodingFormat: "image/jpeg",
                width: 2400,
                height: 1260,
                ...storyCreditSchema(story),
              },
              {
                "@type": "ImageGallery",
                "@id": `${url}#gallery`,
                name: `${copy.headline} — fotos de antes y después`,
                inLanguage: "es-US",
                isPartOf: { "@id": `${url}#webpage` },
                image: story.single
                  ? [{ "@id": `${url}#primaryimage` }]
                  : [{ "@id": `${url}#beforeimage` }, { "@id": `${url}#primaryimage` }],
              },
              {
                "@type": "Article",
                "@id": `${url}#article`,
                headline: copy.headline,
                alternativeHeadline: copy.tagline,
                description,
                url,
                mainEntityOfPage: { "@id": `${url}#webpage` },
                image: [{ "@id": `${url}#primaryimage` }, { "@id": `${url}#socialimage` }],
                author: PHYSICIAN_REF,
                publisher: ORG_REF,
                datePublished: "2026-08-09",
                dateModified: lastmodFor(`/es/antes-y-despues/historia/${params.slug}`) ?? "2026-08-09",
                inLanguage: "es-US",
                articleSection: "Resultados de pacientes",
                keywords: [
                  "cirugía bariátrica antes y después",
                  "resultados de pérdida de peso",
                  "manga gástrica en Virginia",
                  copy.label,
                ],
                articleBody: [copy.caption, copy.quote].filter(Boolean).join(" "),
                associatedMedia: { "@id": `${url}#gallery` },
                ...(copy.quote ? { citation: copy.quote } : {}),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsStoryPage,
});

function EsStoryPage() {
  const { story, slug, index } = Route.useLoaderData();
  const copy = esStoryCopy(story, index);
  const url = `${SITE}/es/antes-y-despues/historia/${slug}`;
  const photoAlt = `${copy.label} — ${
    story.single ? "antes y después" : "resultado"
  } de su cirugía de pérdida de peso en Virginia Bariatric Surgery`;

  return (
    <main className="mx-auto max-w-3xl px-6 py-10" lang="es">
      <nav aria-label="Ruta de navegación" className="text-sm text-muted-foreground">
        <Link to="/es/antes-y-despues" className="hover:underline">
          &larr; Antes y después
        </Link>
      </nav>

      {/* Same Spanish section nav as the other /es templates, so the Spanish
          contact page is one click (and one crawlable link) from every story. */}
      <EsNav className="mt-4" />

      <h1 className="mt-4 font-serif text-3xl text-brand-logo md:text-4xl">{copy.headline}</h1>
      {copy.tagline && <p className="mt-2 text-muted-foreground">{copy.tagline}</p>}

      <figure className="m-0 mt-6 overflow-hidden rounded-2xl border border-border bg-card">
        {story.single ? (
          <img
            src={story.before.url}
            srcSet={story.before.srcSet}
            sizes={story.before.sizes}
            width={story.before.width}
            height={story.before.height}
            alt={photoAlt}
            className="block h-auto w-full"
          />
        ) : (
          <div className="grid grid-cols-2">
            <img
              src={story.before.url}
              srcSet={story.before.srcSet}
              sizes={story.before.sizes}
              alt={`${copy.label} antes de su cirugía de pérdida de peso con el Dr. Matthew Fitzer`}
              style={{ objectPosition: story.beforePosition ?? "center top" }}
              className={`${story.aspectClass ?? "aspect-[4/3]"} w-full object-cover`}
            />
            <img
              src={story.after.url}
              srcSet={story.after.srcSet}
              sizes={story.after.sizes}
              alt={`${copy.label} después de su cirugía de pérdida de peso con el Dr. Matthew Fitzer`}
              style={{ objectPosition: story.afterPosition ?? "center top" }}
              className={`${story.aspectClass ?? "aspect-[4/3]"} w-full object-cover`}
            />
          </div>
        )}
        <figcaption className="px-4 py-3 text-sm text-muted-foreground">
          {copy.caption}
          <small className="mt-1 block text-xs text-muted-foreground/80">
            Crédito: Virginia Bariatric Surgery. Foto de paciente compartida con permiso; sin
            retocar.
          </small>
        </figcaption>
      </figure>

      {copy.quote && (
        <blockquote className="mt-6 border-l-4 border-brand-logo pl-4 text-lg leading-[1.6] text-foreground/80">
          {copy.quote}
        </blockquote>
      )}

      <p className="mt-6 text-sm text-muted-foreground">
        Los resultados varían de un paciente a otro.{" "}
        <a
          href={`/results/story/${slug}`}
          hrefLang="en"
          className="font-medium text-brand-logo hover:underline"
        >
          Ver esta historia en inglés
        </a>
        .
      </p>

      <div className="mt-8">
        <ShareButtons
          url={url}
          title={copy.headline}
          instagramImage={`${SITE}/og/stories/es/ig/${slug}.jpg`}
          lang="es"
        />
      </div>

      <section className="mt-10 text-center">
        <h2 className="font-serif text-2xl">¿Listo para su propio antes y después?</h2>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link
            to="/es/contacto"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Solicitar una consulta
          </Link>
          <Link
            to="/es/antes-y-despues"
            className="rounded-full border border-brand-logo px-7 py-3 text-sm font-medium text-brand-logo hover:bg-brand-soft/50"
          >
            Ver todos los resultados
          </Link>
        </div>
      </section>
    </main>
  );
}
